<script setup>
  import {computed, inject, onMounted, ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {useForm} from "@inertiajs/vue3";
  import {MedButton} from "@pharma4u/medicheck-components";
  import {pdfRouter} from "@utils/pdfRouter/pdfRouter.js";
  import {isAxiosError} from "axios";

  defineOptions({
    name: "ComponentBmpStoreDialog",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    mpUuid: {
      type: String,
      default: null,
    },
  });

  const privacy = inject("$privacy");

  const refDialog = ref(null);

  const internalPatient = ref(null);
  // values from serialized mp
  const firstName = ref(null);
  const lastName = ref(null);
  const hasNameMismatch = ref(false);

  const form = useForm({
    mp: null,
  });

  const error = ref(null);

  const valid = computed(() => {
    return form.mp !== null;
  });

  onMounted(async () => {
    internalPatient.value = await privacy.decryptPatient(props.patient);

    if (props.mpUuid) {
      refDialog.value.open();

      // load serialized mp from pdf service
      pdfRouter.getSerializedMp(props?.mpUuid, {
        onSuccess: (res) => {
          if (res.data.mp) {
            const firstNameMatch = res.data.mp.match(/s:9:"firstName";s:\d+:"([^"]+)";/);
            const lastNameMatch = res.data.mp.match(/s:8:"lastName";s:\d+:"([^"]+)";/);
            firstName.value = firstNameMatch ? firstNameMatch[1] : null;
            lastName.value = lastNameMatch ? lastNameMatch[1] : null;
            if (
              (firstName.value !== null && firstName.value !== internalPatient.value.firstname) ||
              (lastName.value !== null && lastName.value !== internalPatient.value.lastname)
            ) {
              hasNameMismatch.value = true;
            }

            privacy.encryptValue(res.data.mp).then((encrypted) => {
              form.mp = encrypted;
            });
          } else {
            throw new Error("Beim Abruf der Daten aus dem BMP-Manager entsprachen diese nicht dem erwarteten Format.");
          }
        },
        onError: (error_) => {
          if (isAxiosError(error_)) {
            error.value = error_?.response?.data?.message ?? error_.message;
          } else if (error_ instanceof Error) {
            error.value = error_.message;
          } else {
            error.value = "Bei der Kommunikation mit dem BMP-Manager trat ein Fehler auf.";
          }
        },
      });
    }
  });

  function open() {
    refDialog.value.open();
  }

  function close() {
    hasNameMismatch.value = false;
    form.mp = null;
    error.value = null;

    // remove get param from current url
    globalThis.history.replaceState(null, "", globalThis.location.pathname);
    refDialog.value.close();
  }

  function save() {
    form.patch(route("patients.update", {patient: props.patient.id}), {
      onSuccess: () => {
        close();
      },
      onError: () => {
        error.value = "Fehler beim Speichern des BMP im MediCheck.";
      },
    });
  }

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="refDialog"
    :dialog-title="patient.mp_date ? 'BMP überschreiben?' : 'BMP speichern?'"
    :test-id="getTestId('bmp-store')"
  >
    <template #content>
      <ComponentAlert v-if="error" type="error" :test-id="getTestId('bmp-store-dialog-error')">
        <strong class="font-semibold">Fehler:</strong>
        {{ error }} Bitte probieren Sie es erneut.
      </ComponentAlert>

      <ComponentAlert v-if="hasNameMismatch" :test-id="getTestId('bmp-store-dialog-mismatch')">
        <strong class="font-semibold">Achtung:</strong>
        Der Patientenname im BMP ({{ firstName }} {{ lastName }}) weicht vom Namen in der Patientenakte ab.
      </ComponentAlert>

      <p v-if="patient.mp_date">
        Der am {{ patient.mp_date }} für
        <span :data-encrypted="internalPatient.firstname" />
        <span :data-encrypted="internalPatient.lastname" />
        gespeicherte BMP wird überschrieben.
      </p>
      <p v-else>
        Der BMP wird bei
        <span :data-encrypted="internalPatient.firstname" />
        <span :data-encrypted="internalPatient.lastname" />
        gespeichert.
      </p>
    </template>

    <template #actions>
      <MedButton
        variant="primary"
        :disabled="!valid || form.processing"
        :data-test="getTestId('bmp-store-dialog-confirm-button')"
        @click="save()"
      >
        <template v-if="patient.mp_date">Überschreiben</template>
        <template v-else>Speichern</template>
      </MedButton>

      <MedButton :data-test="getTestId('bmp-store-dialog-close-button')" @click="close()">Abbrechen</MedButton>
    </template>
  </ComponentDialog>
</template>
